import { DvGridComponent, DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import ExcelExportUtils from '@core/utils/excel-export.utils';
import {
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvFilterRangeKeys,
  DvFormUtils,
  DvPanelModule,
  DvProgressSpinnerModule,
  FormOf,
} from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GridApi, GridReadyEvent, RowNode } from 'ag-grid-community';
import { debounceTime, distinctUntilChanged, filter, Observable } from 'rxjs';
import { EmissionsGridService } from './emissions-grid.service';
import {
  EmissionDataFilterRequestPayload,
  EmissionDto,
  EmissionsFilterPanelForm,
} from './interfaces';
import { AgGridModule } from 'ag-grid-angular';
import { PipesModule } from '@shared/pipes/pipes.module';
import { AsyncPipe } from '@angular/common';
import { ReportProgressService } from '@core/services/report-progress.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { TripType } from '@pages/trips-page/constants';
import CustomGridFilterUtils from '@core/utils/custom-grid-filter.utils';
import TripTypeUtils from '@shared/utils/trip-type.utils';

@Component({
  imports: [
    DvGridComponent,
    AgGridModule,
    DvDynamicFormModule,
    DvProgressSpinnerModule,
    PipesModule,
    DvPanelModule,
    AsyncPipe,
    DirectivesModule,
    TranslateModule,
  ],
  selector: 'app-emissions-grid',
  templateUrl: './emissions-grid.component.html',
  styleUrls: ['./emissions-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [EmissionsGridService, GridOptionsService],
})
export class EmissionsGridComponent implements OnInit {
  private gridService = inject(EmissionsGridService);
  readonly gridOptionsService = inject(GridOptionsService);
  private translate = inject(TranslateService);
  private api = inject(RestApiService);
  private reportProgressService = inject(ReportProgressService);

  gridOptions: GridOptions;
  selectedNode?: RowNode;
  filterPanelElements: DvDynamicFormElement[];
  formGroup: FormGroup<FormOf<EmissionsFilterPanelForm>>;
  actions: DvToolbarAction[];
  excelLoadingTrigger$: Observable<boolean>;
  reportInProgressText$: Observable<string>;

  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.filterPanelElements = this.gridService.filterPanelElements;
    this.actions = this.gridService.getGridActions(
      this.emissionPrPurposeFiltersFn,
      this.emissionFileNameFn
    );
    this.gridOptions = {
      ...this.gridOptionsService.serverSide(
        this.gridService.getColDefs(),
        Entity.Emission,
        this.getDefaultEmissionsGridFilters(),
        undefined,
        [
          'DistanceInPeriod',
          'CO2InPeriod',
          'KWhInPeriod',
          'KWhLastYear',
          'TripDurationHM',
          'TripsInPeriod',
        ] satisfies (keyof EmissionDto)[]
      ),
      rowGroupPanelShow: 'never',
    };
    this.initFormGroup();
    this.excelLoadingTrigger$ = this.gridService.excelLoadingTrigger$;
    this.reportInProgressText$ = this.reportProgressService.progressText$;
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onGridFiltersCleared(): void {
    this.formGroup.reset();
    this.formGroup.controls.Period?.setValue({
      selectedFilter: {
        title: this.translate.instant(DvFilterRangeKeys.Today),
        filterKey: DvFilterRangeKeys.Today,
      },
    });
    this.formGroup.controls.TripType?.setValue(
      TripTypeUtils.getTripTypeId(TripType.Corporate)
    );
    this.gridOptions.context.extraFilter =
      this.getDefaultEmissionsGridFilters();
    this.gridApi?.setFilterModel({});
  }

  private initFormGroup(): void {
    this.formGroup = DvFormUtils.createFormGroup(this.filterPanelElements);
    this.formGroup.controls.Period?.setValue({
      selectedFilter: {
        title: this.translate.instant(DvFilterRangeKeys.Today),
        filterKey: DvFilterRangeKeys.Today,
      },
    });
    this.formGroup.controls.TripType?.setValue(
      TripTypeUtils.getTripTypeId(TripType.Corporate)
    );
    this.listenToFilterPanelFormChanges();
  }

  private listenToFilterPanelFormChanges(): void {
    this.formGroup.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.formGroup.valid && this.formGroup.dirty)
      )
      .subscribe(val => {
        this.gridOptions.context.extraFilter = undefined;
        this.getFilterModel(val);
      });
  }

  private getFilterRequestPayloadFromForm(
    val: EmissionsFilterPanelForm
  ): EmissionDataFilterRequestPayload {
    return {
      Period: (
        val.Period?.selectedFilter?.filterKey || DvFilterRangeKeys.All
      ).split('.')[1],
      Department: val.Department?.map(departmentId => ({
        DepartmentID: departmentId,
      })),
      Driver: val.Driver?.map(userID => ({ UserID: userID })),
      VehicleTag: val.VehicleTag,
      FilterModel: this.gridApi?.getFilterModel(),
      TripType: [{ TripTypeID: val.TripType }],
    };
  }

  private getFilterModel(formData: EmissionsFilterPanelForm): void {
    const payload: EmissionDataFilterRequestPayload =
      this.getFilterRequestPayloadFromForm(formData);
    this.api
      .postCustom<FilterModelWithKeys>('trip/getTripsFilterModel', payload)
      .subscribe(resp => {
        let filterModel = resp;

        const periodValue = formData.Period;
        if (
          periodValue?.selectedFilter?.filterKey === DvFilterRangeKeys.Custom &&
          periodValue.start &&
          periodValue.end
        ) {
          filterModel = {
            ...filterModel,
            ...CustomGridFilterUtils.getStartTimeCustomPeriodFilters(
              periodValue.start,
              periodValue.end,
              true
            ),
          };
        }

        if (!formData.TripType) {
          delete filterModel['TripTypeID'];
        }

        this.gridApi?.setFilterModel(filterModel);
      });
  }

  private getDefaultEmissionsGridFilters(): FilterModelWithKeys {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      ...CustomGridFilterUtils.getStartTimeCustomPeriodFilters(today, tomorrow),
      ...CustomGridFilterUtils.getCorporateTripTypeFilter(),
    };
  }

  private emissionPrPurposeFiltersFn = (): GetRowsRequest => {
    return {
      entity: Entity.EmissionPrPurpose,
      fields: [],
      filterModel: this.gridApi?.getFilterModel(),
      CustomFilter: this.gridOptions.context.customFilter,
      sortModel: this.gridApi
        ?.getColumnState()
        .filter(col => col.sort !== null)
        .map(function (c) {
          return { sort: c.sort, colId: c.colId };
        }),
      quickFilterText: this.gridOptions.context.quickFilterText,
    } as GetRowsRequest;
  };
  private emissionFileNameFn = (): string => {
    return ExcelExportUtils.getCustomPeriodExcelFileName(
      this.formGroup.controls.Period?.value
    );
  };
}
